import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Activity, AlertTriangle, Package, FileText, CheckCircle, Shield,
  Users, Clock, DollarSign, Clipboard, Heart, Zap, ChevronRight,
  AlertCircle, UserCheck, ClipboardList, Home, Stethoscope
} from "lucide-react";

const SkinAutomationPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('trigger');
  const [loading, setLoading] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const [activeWounds, setActiveWounds] = useState(null);
  const [bradenScore, setBradenScore] = useState(null);
  
  const [wound, setWound] = useState({
    patient_id: '',
    patient_name: '',
    chart_id: '',
    location: 'sacrum',
    stage: 'stage_2',
    length_cm: '',
    width_cm: '',
    depth_cm: '',
    tunneling_present: false,
    undermining_present: false,
    wound_bed_tissue: 'granulation',
    exudate_type: 'serous',
    exudate_amount: 'small',
    periwound_condition: 'Intact',
    odor_present: false,
    pain_level: 3,
    photo_attached: false,
    assessed_by: user?.name || 'RN'
  });

  const [braden, setBraden] = useState({
    patient_id: '',
    sensory_perception: 3,
    moisture: 3,
    activity: 2,
    mobility: 2,
    nutrition: 3,
    friction_shear: 2
  });

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'administrator', 'staff'].includes(user.role))) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    loadActiveWounds();
  }, []);

  const loadActiveWounds = async () => {
    try {
      const response = await api.get('/ehr/skin-automation/dashboard/active-wounds');
      setActiveWounds(response.data);
    } catch (error) {
      console.error("Failed to load active wounds:", error);
    }
  };

  const handleTriggerWorkflow = async () => {
    if (!wound.patient_name) {
      toast.error("Please enter patient name");
      return;
    }

    setLoading(true);
    try {
      const woundPayload = {
        ...wound,
        length_cm: wound.length_cm ? parseFloat(wound.length_cm) : null,
        width_cm: wound.width_cm ? parseFloat(wound.width_cm) : null,
        depth_cm: wound.depth_cm ? parseFloat(wound.depth_cm) : null
      };
      
      const response = await api.post('/ehr/skin-automation/trigger', {
        wound: woundPayload
      });
      setTriggerResult(response.data);
      
      if (response.data.compliance_alerts?.length > 0) {
        toast.warning("Compliance alerts generated - see documentation requirements", { duration: 4000 });
      } else {
        toast.success("Skin impairment workflow triggered successfully!");
      }
      
      if (response.data.idg_flag?.priority === 'high') {
        toast.info("Patient flagged for IDG Hot List", { duration: 3000 });
      }
    } catch (error) {
      console.error("Trigger failed:", error);
      toast.error(error.response?.data?.detail || "Failed to trigger workflow");
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateBraden = async () => {
    setLoading(true);
    try {
      const response = await api.post('/ehr/skin-automation/braden-scale/calculate', {
        ...braden,
        patient_id: wound.patient_id || 'demo'
      });
      setBradenScore(response.data);
      toast.success(`Braden Score: ${response.data.total_score} - ${response.data.risk_level}`);
    } catch (error) {
      console.error("Braden calculation failed:", error);
      toast.error("Failed to calculate Braden score");
    } finally {
      setLoading(false);
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'stage_4': return 'bg-red-100 text-red-700';
      case 'stage_3': return 'bg-orange-100 text-orange-700';
      case 'stage_2': return 'bg-yellow-100 text-yellow-700';
      case 'stage_1': return 'bg-green-100 text-green-700';
      default: return 'bg-purple-100 text-purple-700';
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'bg-green-100 text-green-700';
      case 'B': return 'bg-yellow-100 text-yellow-700';
      case 'C': return 'bg-orange-100 text-orange-700';
      default: return 'bg-red-100 text-red-700';
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Heart className="w-8 h-8 text-orange-600" />
                Skin Impairment Automation
              </h1>
              <p className="text-muted-foreground mt-1">
                Body Map → Orders → Care Plan → IDG → HOPE Section M
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/ehr-compliance')}>
              Back to EHR
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {activeWounds && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Wounds</p>
                    <p className="text-2xl font-bold">{activeWounds.total_active_wounds}</p>
                  </div>
                  <Heart className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Improving</p>
                    <p className="text-2xl font-bold text-green-600">{activeWounds.healing_status?.improving || 0}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Worsening</p>
                    <p className="text-2xl font-bold text-red-600">{activeWounds.healing_status?.worsening || 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Stage 3/4</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {(activeWounds.by_stage?.stage_3 || 0) + (activeWounds.by_stage?.stage_4 || 0)}
                    </p>
                  </div>
                  <Activity className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">DME Pending</p>
                    <p className="text-2xl font-bold">{activeWounds.dme_pending || 0}</p>
                  </div>
                  <Package className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="trigger" data-testid="tab-trigger">
              <Zap className="w-4 h-4 mr-2" />
              Body Map Trigger
            </TabsTrigger>
            <TabsTrigger value="braden" data-testid="tab-braden">
              <Activity className="w-4 h-4 mr-2" />
              Braden Scale
            </TabsTrigger>
            <TabsTrigger value="results" data-testid="tab-results">
              <ClipboardList className="w-4 h-4 mr-2" />
              Automation Results
            </TabsTrigger>
          </TabsList>

          {/* Body Map Trigger Tab */}
          <TabsContent value="trigger">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-orange-600" />
                      Wound Assessment
                    </CardTitle>
                    <CardDescription>
                      Select location and stage to trigger full automation workflow
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Patient Name *</Label>
                        <Input
                          value={wound.patient_name}
                          onChange={(e) => setWound(prev => ({ ...prev, patient_name: e.target.value }))}
                          placeholder="Enter patient name"
                          data-testid="patient-name-input"
                        />
                      </div>
                      <div>
                        <Label>Patient ID</Label>
                        <Input
                          value={wound.patient_id}
                          onChange={(e) => setWound(prev => ({ ...prev, patient_id: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Wound Location (Body Map)</Label>
                        <Select 
                          value={wound.location}
                          onValueChange={(value) => setWound(prev => ({ ...prev, location: value }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sacrum">Sacrum</SelectItem>
                            <SelectItem value="coccyx">Coccyx</SelectItem>
                            <SelectItem value="left_heel">Left Heel</SelectItem>
                            <SelectItem value="right_heel">Right Heel</SelectItem>
                            <SelectItem value="left_trochanter">Left Trochanter</SelectItem>
                            <SelectItem value="right_trochanter">Right Trochanter</SelectItem>
                            <SelectItem value="left_ischium">Left Ischium</SelectItem>
                            <SelectItem value="right_ischium">Right Ischium</SelectItem>
                            <SelectItem value="occiput">Occiput</SelectItem>
                            <SelectItem value="scapula">Scapula</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Wound Stage</Label>
                        <Select 
                          value={wound.stage}
                          onValueChange={(value) => setWound(prev => ({ ...prev, stage: value }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="stage_1">Stage 1 - Non-blanchable erythema</SelectItem>
                            <SelectItem value="stage_2">Stage 2 - Partial thickness</SelectItem>
                            <SelectItem value="stage_3">Stage 3 - Full thickness</SelectItem>
                            <SelectItem value="stage_4">Stage 4 - Full thickness with tissue loss</SelectItem>
                            <SelectItem value="unstageable">Unstageable</SelectItem>
                            <SelectItem value="deep_tissue">Deep Tissue Injury</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Measurements for Stage 2+ */}
                    {wound.stage !== 'stage_1' && (
                      <div className="p-4 border rounded-lg bg-orange-50">
                        <Label className="font-semibold mb-2 block">Measurements (Required for LCD Compliance)</Label>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label className="text-sm">Length (cm)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={wound.length_cm}
                              onChange={(e) => setWound(prev => ({ ...prev, length_cm: e.target.value }))}
                              placeholder="0.0"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Width (cm)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={wound.width_cm}
                              onChange={(e) => setWound(prev => ({ ...prev, width_cm: e.target.value }))}
                              placeholder="0.0"
                            />
                          </div>
                          {['stage_3', 'stage_4'].includes(wound.stage) && (
                            <div>
                              <Label className="text-sm">Depth (cm)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={wound.depth_cm}
                                onChange={(e) => setWound(prev => ({ ...prev, depth_cm: e.target.value }))}
                                placeholder="0.0"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Wound Bed Tissue</Label>
                        <Select 
                          value={wound.wound_bed_tissue}
                          onValueChange={(value) => setWound(prev => ({ ...prev, wound_bed_tissue: value }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="epithelial">Epithelial (Pink)</SelectItem>
                            <SelectItem value="granulation">Granulation (Red)</SelectItem>
                            <SelectItem value="slough">Slough (Yellow)</SelectItem>
                            <SelectItem value="eschar">Eschar (Black)</SelectItem>
                            <SelectItem value="necrotic">Necrotic</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Exudate Type</Label>
                        <Select 
                          value={wound.exudate_type}
                          onValueChange={(value) => setWound(prev => ({ ...prev, exudate_type: value }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="serous">Serous (Clear)</SelectItem>
                            <SelectItem value="serosanguinous">Serosanguinous (Pink)</SelectItem>
                            <SelectItem value="sanguinous">Sanguinous (Bloody)</SelectItem>
                            <SelectItem value="purulent">Purulent (Yellow/Green)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={wound.tunneling_present}
                          onCheckedChange={(checked) => setWound(prev => ({ ...prev, tunneling_present: checked }))}
                        />
                        <Label>Tunneling Present</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={wound.undermining_present}
                          onCheckedChange={(checked) => setWound(prev => ({ ...prev, undermining_present: checked }))}
                        />
                        <Label>Undermining Present</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={wound.odor_present}
                          onCheckedChange={(checked) => setWound(prev => ({ ...prev, odor_present: checked }))}
                        />
                        <Label>Odor Present</Label>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={wound.photo_attached}
                        onCheckedChange={(checked) => setWound(prev => ({ ...prev, photo_attached: checked }))}
                      />
                      <Label>Photo Documentation Attached</Label>
                    </div>

                    <Button 
                      onClick={handleTriggerWorkflow}
                      disabled={loading}
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      data-testid="trigger-workflow-btn"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      Trigger Skin Impairment Workflow
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Reference */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Automation Triggers</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="p-2 bg-green-50 rounded">
                      <p className="font-medium text-green-700">Stage 1-2</p>
                      <p className="text-xs text-green-600">Supplies + Care Plan</p>
                    </div>
                    <div className="p-2 bg-orange-50 rounded">
                      <p className="font-medium text-orange-700">Stage 3</p>
                      <p className="text-xs text-orange-600">+ Air Mattress + MD Orders</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded">
                      <p className="font-medium text-red-700">Stage 4</p>
                      <p className="text-xs text-red-600">+ Low Air Loss Mattress + IDG Priority</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">LCD Grade A Requirements</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <p>✓ 3D Measurements (L x W x D)</p>
                    <p>✓ Link to terminal diagnosis</p>
                    <p>✓ Braden score documented</p>
                    <p>✓ Photo for Stage 3/4</p>
                    <p>✓ Weekly measurements</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Braden Scale Tab */}
          <TabsContent value="braden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Braden Scale Assessment</CardTitle>
                  <CardDescription>Calculate pressure injury risk (Score 6-23)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { key: 'sensory_perception', label: 'Sensory Perception', max: 4 },
                    { key: 'moisture', label: 'Moisture', max: 4 },
                    { key: 'activity', label: 'Activity', max: 4 },
                    { key: 'mobility', label: 'Mobility', max: 4 },
                    { key: 'nutrition', label: 'Nutrition', max: 4 },
                    { key: 'friction_shear', label: 'Friction & Shear', max: 3 }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between">
                      <Label>{item.label}</Label>
                      <Select 
                        value={braden[item.key].toString()}
                        onValueChange={(value) => setBraden(prev => ({ ...prev, [item.key]: parseInt(value) }))}
                      >
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: item.max }, (_, i) => i + 1).map(n => (
                            <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  <Button onClick={handleCalculateBraden} disabled={loading} className="w-full">
                    Calculate Braden Score
                  </Button>
                </CardContent>
              </Card>

              {bradenScore && (
                <Card>
                  <CardHeader>
                    <CardTitle>Braden Score Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center mb-4">
                      <p className="text-5xl font-bold">{bradenScore.total_score}</p>
                      <Badge className={
                        bradenScore.total_score <= 9 ? 'bg-red-100 text-red-700' :
                        bradenScore.total_score <= 12 ? 'bg-orange-100 text-orange-700' :
                        bradenScore.total_score <= 14 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }>
                        {bradenScore.risk_level}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <p className="font-medium">Recommendations:</p>
                      {bradenScore.recommendations?.map((rec, idx) => (
                        <p key={idx} className="text-sm flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {rec}
                        </p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results">
            {triggerResult ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Documentation Grade */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Documentation Grade</span>
                      <Badge className={getGradeColor(triggerResult.documentation_grade)}>
                        Grade {triggerResult.documentation_grade}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {triggerResult.compliance_alerts?.length > 0 ? (
                      <div className="space-y-2">
                        {triggerResult.compliance_alerts.map((alert, idx) => (
                          <div key={idx} className="p-2 bg-amber-50 rounded border border-amber-200">
                            <p className="text-sm text-amber-700">{alert}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-green-600">All documentation requirements met!</p>
                    )}
                  </CardContent>
                </Card>

                {/* Physician Orders */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Stethoscope className="w-5 h-5" />
                      Auto-Generated Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {triggerResult.physician_orders?.map((order, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-start">
                            <span className="font-medium">{order.order_name}</span>
                            <Badge variant="outline">{order.status}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{order.frequency}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Supplies */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Supply Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {triggerResult.supply_orders?.map((supply, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 border rounded">
                          <span>{supply.supply_name}</span>
                          <Badge variant="outline">{supply.delivery_priority}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* DME */}
                {triggerResult.dme_orders?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>DME Orders</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {triggerResult.dme_orders.map((dme, idx) => (
                          <div key={idx} className="p-3 border rounded-lg bg-blue-50">
                            <span className="font-medium">{dme.dme_name}</span>
                            <Badge className="ml-2 bg-blue-100 text-blue-700">{dme.delivery_priority}</Badge>
                            <p className="text-sm text-muted-foreground mt-1">{dme.clinical_justification}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Care Plan & Aide Tasks */}
                <Card>
                  <CardHeader>
                    <CardTitle>Care Plan Updates</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-3 bg-purple-50 rounded-lg mb-3">
                      <p className="font-medium text-purple-700">New Problem:</p>
                      <p className="text-sm">{triggerResult.care_plan_updates?.new_problem?.statement}</p>
                    </div>
                    <p className="font-medium mb-2">Aide Tasks:</p>
                    <ul className="space-y-1">
                      {triggerResult.aide_tasks?.map((task, idx) => (
                        <li key={idx} className="text-sm flex items-start gap-2">
                          <UserCheck className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {task}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* IDG & Cost */}
                <Card>
                  <CardHeader>
                    <CardTitle>IDG & Cost Impact</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className={`p-3 rounded-lg ${
                        triggerResult.idg_flag?.priority === 'high' ? 'bg-red-50' : 'bg-blue-50'
                      }`}>
                        <p className="font-medium">IDG Status</p>
                        <Badge className={
                          triggerResult.idg_flag?.priority === 'high' ? 'bg-red-100 text-red-700' : ''
                        }>
                          {triggerResult.idg_flag?.priority?.toUpperCase()} Priority
                        </Badge>
                        <p className="text-sm mt-1">{triggerResult.idg_flag?.agenda_note}</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="font-medium">Cost Impact</p>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                          <span>Monthly Supplies:</span>
                          <span className="font-bold">${triggerResult.cost_impact?.monthly_supply_cost}</span>
                          <span>DME:</span>
                          <span className="font-bold">${triggerResult.cost_impact?.dme_cost}</span>
                          <span>PPD Increase:</span>
                          <span className="font-bold">+${triggerResult.cost_impact?.ppd_increase}/day</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Heart className="w-12 h-12 mx-auto text-muted-foreground opacity-30 mb-2" />
                  <p className="text-muted-foreground">Trigger a wound assessment to see automation results</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SkinAutomationPage;
